import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Fingerprint,
  KeyRound,
  ScrollText,
  ShieldCheck,
  UserCog,
} from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { fetchOnboardingProgress, setOnboardingStep, AUTH_STR as S } from './authAdminApi'

/**
 * A1.08 — Добро пожаловать / онбординг. Informational checklist for a new
 * admin. Steps are static (module-local `AUTH_STR`); completion flags are
 * fetched fail-soft from `/admin/account/onboarding` (empty until it ships,
 * so every step reads as «todo»). No backend dependency to render.
 */
interface Step {
  id: string
  icon: ReactNode
  title: string
  desc: string
  route: string
}

export function OnboardingScreen() {
  const [done, setDone] = useState<Set<string>>(new Set())

  const steps = useMemo<Step[]>(
    () => [
      { id: 'profile', icon: <UserCog size={18} />, title: S.stepProfileTitle, desc: S.stepProfileDesc, route: '/account' },
      { id: '2fa', icon: <ShieldCheck size={18} />, title: S.step2faTitle, desc: S.step2faDesc, route: '/account/security' },
      { id: 'hardware_key', icon: <Fingerprint size={18} />, title: S.stepKeyTitle, desc: S.stepKeyDesc, route: '/auth/hardware-2fa' },
      { id: 'roles', icon: <KeyRound size={18} />, title: S.stepRolesTitle, desc: S.stepRolesDesc, route: '/staff' },
      { id: 'audit', icon: <ScrollText size={18} />, title: S.stepAuditTitle, desc: S.stepAuditDesc, route: '/security/audit' },
    ],
    [],
  )

  useEffect(() => {
    const ac = new AbortController()
    fetchOnboardingProgress(ac.signal)
      .then(setDone)
      .catch(() => setDone(new Set()))
    return () => ac.abort()
  }, [])

  // Toggle a step's completion (optimistic; reverts on error). PUT persists it
  // per-admin so the progress bar reflects real stored state next load.
  const toggle = async (id: string, complete: boolean) => {
    setDone((prev) => {
      const next = new Set(prev)
      if (complete) next.add(id)
      else next.delete(id)
      return next
    })
    try {
      await setOnboardingStep(id, complete)
    } catch {
      setDone((prev) => {
        const next = new Set(prev)
        if (complete) next.delete(id)
        else next.add(id)
        return next
      })
    }
  }

  const doneCount = steps.filter((s) => done.has(s.id)).length
  const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0

  return (
    <>
      <PageHeader title={S.onboarding} screenId="A1.08" description={S.onboardingDesc} />
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <div className="mx-auto w-full max-w-[720px] space-y-4">
          {/* Welcome + progress */}
          <div
            className="rounded-lg border border-border-default bg-surface-2 p-4"
            style={{ boxShadow: 'var(--shadow-panel)' }}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-subtle text-accent">
                <ShieldCheck size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-primary">{S.welcomeTitle}</h2>
                <p className="mt-0.5 text-sm text-secondary">{S.welcomeBody}</p>
              </div>
            </div>

            <div className="mt-3">
              <div className="mb-1 flex items-center justify-between text-xs text-tertiary">
                <span>{S.progressLabel}</span>
                <span className="font-mono">
                  {doneCount}/{steps.length} · {pct}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                <div
                  className="h-full rounded-full bg-accent transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Checklist */}
          <ul className="space-y-2">
            {steps.map((s) => {
              const complete = done.has(s.id)
              return (
                <li
                  key={s.id}
                  className="flex items-start gap-3 rounded-lg border border-border-default bg-surface-2 p-3 transition-colors hover:border-accent"
                >
                  {/* Real toggle → PUT /admin/account/onboarding */}
                  <button
                    type="button"
                    onClick={() => toggle(s.id, !complete)}
                    title={complete ? S.markUndo : S.markDone}
                    className={
                      complete
                        ? 'mt-0.5 shrink-0 text-success'
                        : 'mt-0.5 shrink-0 text-tertiary hover:text-secondary'
                    }
                  >
                    {complete ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                  </button>
                  <Link to={s.route} className="flex min-w-0 flex-1 items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="shrink-0 text-secondary">{s.icon}</span>
                        <span className="truncate text-sm font-medium text-primary">{s.title}</span>
                        <span
                          className={
                            complete
                              ? 'ml-1 rounded-sm bg-success-subtle px-1.5 py-0.5 text-xs font-medium text-success'
                              : 'ml-1 rounded-sm bg-surface-3 px-1.5 py-0.5 text-xs font-medium text-tertiary'
                          }
                        >
                          {complete ? S.stepDone : S.stepTodo}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-xs text-secondary">{s.desc}</span>
                    </span>
                    <span className="mt-0.5 flex shrink-0 items-center gap-1 text-xs text-accent">
                      {S.open}
                      <ArrowRight size={13} />
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </>
  )
}
